import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { Link } from 'react-router-dom';

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState('myProperties');
  const [myProperties, setMyProperties] = useState([]);
  const [savedProperties, setSavedProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!auth.currentUser) return;
      
      try {
        setLoading(true);
        // 1. Fetch properties posted by current user
        const q = query(collection(db, "properties"), where("ownerEmail", "==", auth.currentUser.email));
        const querySnapshot = await getDocs(q);
        const userProps = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setMyProperties(userProps);

        // 2. Fetch user's saved/wishlisted properties from user doc or localStorage
        // Here we use localStorage/Firestore fallback for quick persistence
        const savedIds = JSON.parse(localStorage.getItem(`saved_${auth.currentUser.uid}`)) || [];
        if (savedIds.length > 0) {
          const savedList = [];
          for (const id of savedIds) {
            const propDoc = await getDoc(doc(db, "properties", id));
            if (propDoc.exists()) {
              savedList.push({ id: propDoc.id, ...propDoc.data() });
            }
          }
          setSavedProperties(savedList);
        } else {
          setSavedProperties([]);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data: ", error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Delete property handler
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      try {
        await deleteDoc(doc(db, "properties", id));
        setMyProperties(myProperties.filter(p => p.id !== id));
      } catch (error) {
        console.error("Error deleting property:", error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        
        {/* User Info Header */}
        <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 mb-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <span className="text-xs uppercase tracking-wider text-indigo-600 font-semibold">User Dashboard</span>
            <h1 className="text-2xl font-extrabold text-slate-900 mt-1">{auth.currentUser?.email}</h1>
          </div>
          <div className="flex bg-slate-100 p-1.5 rounded-2xl">
            <button 
              onClick={() => setActiveTab('myProperties')}
              className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition ${activeTab === 'myProperties' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-600'}`}
            >
              My Listings ({myProperties.length})
            </button>
            <button 
              onClick={() => setActiveTab('savedProperties')}
              className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition ${activeTab === 'savedProperties' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-600'}`}
            >
              Saved Wishlist ({savedProperties.length})
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-500 font-medium">Loading your dashboard...</div>
        ) : activeTab === 'myProperties' ? (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-6">Properties Posted By You</h2>
            {myProperties.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-slate-100">
                <p className="text-slate-500 mb-4">You haven't posted any properties yet.</p>
                <Link to="/add-property" className="bg-indigo-600 text-white px-6 py-3 rounded-xl text-sm font-medium hover:bg-indigo-700 transition">Post Property Now</Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {myProperties.map(property => (
                  <div key={property.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden flex flex-col justify-between">
                    <div>
                      <img src={property.image} alt={property.title} className="w-full h-44 object-cover" />
                      <div className="p-5">
                        <span className="text-xs bg-indigo-50 text-indigo-600 font-semibold px-2.5 py-1 rounded-md">{property.type}</span>
                        <h3 className="font-bold text-slate-900 mt-2 line-clamp-1">{property.title}</h3>
                        <p className="text-slate-500 text-xs mt-1">{property.location}</p>
                        <p className="text-indigo-600 font-extrabold mt-3">₹ {property.price}</p>
                      </div>
                    </div>
                    <div className="p-5 pt-0 flex gap-2">
                      <Link to={`/property/${property.id}`} className="flex-1 bg-slate-100 text-slate-700 text-center py-2 rounded-xl text-xs font-semibold hover:bg-slate-200 transition">View</Link>
                      <button onClick={() => handleDelete(property.id)} className="flex-1 bg-rose-50 text-rose-600 py-2 rounded-xl text-xs font-semibold hover:bg-rose-100 transition">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-6">Your Saved Wishlist</h2>
            {savedProperties.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-slate-100">
                <p className="text-slate-500">No properties saved in your wishlist yet. Click the heart icon on any property to save it here!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {savedProperties.map(property => (
                  <div key={property.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden flex flex-col justify-between">
                    <div>
                      <img src={property.image} alt={property.title} className="w-full h-44 object-cover" />
                      <div className="p-5">
                        <span className="text-xs bg-indigo-50 text-indigo-600 font-semibold px-2.5 py-1 rounded-md">{property.type}</span>
                        <h3 className="font-bold text-slate-900 mt-2 line-clamp-1">{property.title}</h3>
                        <p className="text-slate-500 text-xs mt-1">{property.location}</p>
                        <p className="text-indigo-600 font-extrabold mt-3">₹ {property.price}</p>
                      </div>
                    </div>
                    <div className="p-5 pt-0">
                      <Link to={`/property/${property.id}`} className="block w-full bg-slate-900 text-white text-center py-2.5 rounded-xl text-xs font-semibold hover:bg-indigo-600 transition">View Details</Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}